class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('keyup', (e) => e.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.installReferenceStyles();
    this.addEventListener('submit', (e) => {
      const form = e.target.closest('[data-iw-discount-form]');
      if (!form) return;
      e.preventDefault();
      const code = form.querySelector('input')?.value.trim();
      if (code) this.updateDiscount(code);
    });
    this.addEventListener('click', (e) => {
      if (!e.target.closest('[data-iw-discount-remove]')) return;
      e.preventDefault();
      this.updateDiscount('');
    });
    requestAnimationFrame(() => this.decorate());
    const hook = () => {
      if (this._iwHooked || typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
      this._iwHooked = true;
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        this.syncCount(event?.cartData?.item_count);
        setTimeout(() => this.decorate(), 0);
      });
    };
    hook();
    document.addEventListener('DOMContentLoaded', hook, { once: true });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (e) => { e.preventDefault(); this.open(cartLink); });
    cartLink.addEventListener('keydown', (e) => {
      if (e.code.toUpperCase() === 'SPACE') { e.preventDefault(); this.open(cartLink); }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.decorate();
    if (this.classList.contains('active')) return;
    const note = this.querySelector('[id^="Details-"] summary');
    if (note && !note.hasAttribute('role')) this.setSummaryAccessibility(note);
    setTimeout(() => this.classList.add('animate', 'active'));
    this.addEventListener('transitionend', () => {
      const box = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focus = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      if (box && focus) trapFocus(box, focus);
    }, { once: true });
    document.body.classList.add('overflow-hidden');
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(note) {
    note.setAttribute('role', 'button');
    note.setAttribute('aria-expanded', 'false');
    if (note.nextElementSibling?.id) note.setAttribute('aria-controls', note.nextElementSibling.id);
    note.addEventListener('click', (e) => e.currentTarget.setAttribute('aria-expanded', !e.currentTarget.closest('details').hasAttribute('open')));
    note.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(state) {
    if (typeof state?.item_count === 'number') this.classList.toggle('is-empty', state.item_count === 0);
    else this.classList.remove('is-empty');
    this.productId = state?.id;
    const sections = state?.sections || {};
    this.getSectionsToRender().forEach((section) => {
      const el = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      const html = sections[section.id];
      if (!el || !html) return;
      const inner = this.getSectionInnerHTML(html, section.selector);
      if (inner != null) el.innerHTML = inner;
    });
    if (!this.classList.contains('is-empty')) this.querySelector('cart-drawer-items')?.classList.remove('is-empty');
    this.syncCount(state?.item_count);
    this.decorate();
    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
      this.decorate();
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const node = html ? new DOMParser().parseFromString(html, 'text/html').querySelector(selector) : null;
    return node ? node.innerHTML : null;
  }

  syncCount(count) {
    const apply = (n) => document.querySelectorAll('.chh-cart-count').forEach((el) => {
      el.textContent = n;
      el.classList.toggle('is-empty', n === 0);
    });
    if (typeof count === 'number') return apply(count);
    fetch(`${window.routes?.cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } })
      .then((r) => r.ok ? r.json() : null).then((cart) => cart && apply(cart.item_count)).catch(() => {});
  }

  decorate() {
    if (this.classList.contains('is-empty')) return;
    const footer = this.querySelector('.drawer__footer');
    if (!footer) return;
    const label = footer.querySelector('.totals__total');
    if (label) label.textContent = 'Subtotal';
    const value = footer.querySelector('.totals__total-value');
    if (value) value.textContent = value.textContent.replace(/\s+INR\s*$/i, '').trim();
    const tax = footer.querySelector('.tax-note');
    if (tax) tax.textContent = 'Shipping and taxes calculated at checkout.';
    const summary = footer.querySelector('.cart-drawer__footer');
    if (summary && !summary.querySelector('[data-iw-discount-form]')) {
      const area = document.createElement('div');
      area.className = 'iw-cart-discount';
      area.innerHTML = '<form data-iw-discount-form><label class="visually-hidden" for="IWCartDiscount">Discount code</label><input id="IWCartDiscount" type="text" placeholder="Discount code" autocomplete="off"><button type="submit">APPLY</button></form><p data-iw-discount-message aria-live="polite"></p>';
      const oldDiscounts = summary.querySelector(':scope > div:first-child');
      if (oldDiscounts?.querySelector('.discounts')) {
        oldDiscounts.classList.add('iw-applied-discount');
        oldDiscounts.insertAdjacentHTML('beforeend', '<button type="button" data-iw-discount-remove>Remove</button>');
        area.prepend(oldDiscounts);
      }
      tax ? tax.insertAdjacentElement('afterend', area) : summary.appendChild(area);
    }
    const checkout = footer.querySelector('#CartDrawer-Checkout');
    if (checkout) checkout.innerHTML = '<span>CONTINUE TO CHECKOUT</span><span aria-hidden="true">→</span>';
  }

  updateDiscount(code) {
    const msg = this.querySelector('[data-iw-discount-message]');
    if (msg) msg.textContent = '';
    const root = window.Shopify?.routes?.root || '/';
    fetch(`${root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ discount: code, sections: this.getSectionsToRender().map((s) => s.id), sections_url: location.pathname })
    }).then((r) => r.json().then((data) => { if (!r.ok || data.status) throw data; return data; }))
      .then((cart) => this.renderContents(cart))
      .catch((err) => {
        const current = this.querySelector('[data-iw-discount-message]');
        if (current) current.textContent = err?.description || err?.message || 'Unable to apply this discount code.';
      });
  }

  installReferenceStyles() {
    if (document.getElementById('IWCartReferenceCSS')) return;
    const style = document.createElement('style');
    style.id = 'IWCartReferenceCSS';
    style.textContent = `
cart-drawer:not(.is-empty) .drawer__inner{width:min(400px,100vw)!important;max-width:100vw!important;height:100%!important;padding:0!important;display:flex!important;flex-direction:column!important;overflow:hidden!important;border:0!important;background:#F6F0E8!important;color:#4A332D!important;box-shadow:0 0 50px rgba(0,0,0,.2)!important}
@media(min-width:750px){cart-drawer:not(.is-empty) .drawer__header{display:none!important}}
cart-drawer:not(.is-empty) cart-drawer-items{display:block!important;flex:1 1 auto!important;min-height:0!important;overflow:hidden!important}
cart-drawer:not(.is-empty) .cart-drawer__form,cart-drawer:not(.is-empty) #CartDrawer-CartItems,cart-drawer:not(.is-empty) .drawer__cart-items-wrapper{height:100%!important;min-height:0!important}
cart-drawer:not(.is-empty) #CartDrawer-CartItems{overflow-y:auto!important;overflow-x:hidden!important;padding:0 16px!important}
cart-drawer:not(.is-empty) .cart-items,cart-drawer:not(.is-empty) .cart-items tbody{display:block!important;width:100%!important}cart-drawer:not(.is-empty) .cart-items thead{display:none!important}
cart-drawer:not(.is-empty) .cart-item{display:grid!important;grid-template-columns:96px minmax(0,1fr)!important;grid-template-areas:"m d" "m q"!important;column-gap:16px!important;row-gap:8px!important;width:100%!important;padding:16px 0!important;margin:0!important;border-bottom:1px solid #C8C0B4!important;align-items:start!important}
cart-drawer:not(.is-empty) .cart-item>td{padding:0!important;border:0!important;min-width:0!important}.cart-drawer .cart-item__media{grid-area:m!important;width:96px!important}.cart-drawer .cart-item__media .cart-item__link,.cart-drawer .cart-item__image{width:96px!important;height:112px!important;max-width:none!important;object-fit:cover!important}
cart-drawer:not(.is-empty) .cart-item__details{grid-area:d!important;width:auto!important;color:#4A332D!important;font-family:Manrope,Arial,sans-serif!important}cart-drawer:not(.is-empty) .cart-item__name,cart-drawer:not(.is-empty) .cart-item__name.h4{margin:0!important;color:#4A332D!important;font-family:"Playfair Display",Georgia,serif!important;font-size:16px!important;line-height:1.35!important;font-weight:400!important;text-decoration:none!important}
cart-drawer:not(.is-empty) .cart-item__details>.product-option,cart-drawer:not(.is-empty) .cart-item__discounted-prices{margin-top:6px!important;color:#4A332D!important;font-size:14px!important;font-weight:600!important}cart-drawer:not(.is-empty) .cart-item__details dl{color:rgba(74,51,45,.62)!important;font-size:11px!important}.cart-drawer .cart-item__totals{display:none!important}
cart-drawer:not(.is-empty) .cart-item__quantity{grid-area:q!important;width:auto!important;padding:0!important}cart-drawer:not(.is-empty) .cart-item__quantity-wrapper{display:flex!important;align-items:center!important;gap:8px!important}.cart-drawer .quantity-popover-container{margin:0!important}
cart-drawer:not(.is-empty) .cart-quantity.quantity{width:106px!important;height:34px!important;min-height:34px!important;display:grid!important;grid-template-columns:32px 42px 32px!important;border:1px solid #C8C0B4!important;background:transparent!important;border-radius:0!important}.cart-drawer .cart-quantity.quantity:before,.cart-drawer .cart-quantity.quantity:after{display:none!important}
cart-drawer:not(.is-empty) .cart-quantity .quantity__button,cart-drawer:not(.is-empty) .cart-quantity .quantity__input{width:100%!important;height:32px!important;min-height:0!important;padding:0!important;border:0!important;background:transparent!important;color:#4A332D!important;font-size:13px!important;box-shadow:none!important}.cart-drawer .cart-remove-button{width:34px!important;height:34px!important;min-width:34px!important;min-height:34px!important;margin:0!important;padding:8px!important;border:0!important;background:transparent!important;color:rgba(74,51,45,.45)!important;box-shadow:none!important}
cart-drawer:not(.is-empty) .quantity-popover__info-button,cart-drawer:not(.is-empty) .cart-items__info{display:none!important}
cart-drawer:not(.is-empty) .drawer__footer{flex:0 0 auto!important;padding:0!important;border-top:1px solid #C8C0B4!important;background:#F6F0E8!important;color:#4A332D!important;font-family:Manrope,Arial,sans-serif!important}cart-drawer:not(.is-empty) .drawer__footer>details{display:none!important}
cart-drawer:not(.is-empty) .cart-drawer__footer{display:flex!important;flex-direction:column!important;padding:16px!important;margin:0!important;border:0!important;background:transparent!important}.cart-drawer .cart-drawer__footer>.totals{order:1!important;display:flex!important;justify-content:space-between!important;align-items:center!important;margin:0!important}.cart-drawer .totals__total{margin:0!important;color:rgba(74,51,45,.7)!important;font-size:14px!important;font-weight:400!important}.cart-drawer .totals__total-value{margin:0!important;color:#4A332D!important;font-size:16px!important;font-weight:600!important}
cart-drawer:not(.is-empty) .tax-note{order:2!important;margin:8px 0 0!important;color:rgba(74,51,45,.5)!important;font-size:12px!important;text-align:left!important}.iw-cart-discount{order:3!important;margin-top:16px!important;padding-top:16px!important;border-top:1px solid #C8C0B4!important}.iw-cart-discount form{display:grid!important;grid-template-columns:minmax(0,1fr) auto!important;gap:8px!important}.iw-cart-discount input{height:42px!important;min-width:0!important;padding:0 12px!important;border:1px solid #C8C0B4!important;border-radius:0!important;background:#fff!important;color:#4A332D!important;font:14px Manrope,Arial,sans-serif!important;box-shadow:none!important;outline:0!important}.iw-cart-discount input:focus{border-color:#C8286E!important}.iw-cart-discount form button{height:42px!important;min-width:78px!important;padding:0 14px!important;border:1px solid #C8286E!important;background:transparent!important;color:#C8286E!important;font:600 12px Manrope,Arial,sans-serif!important;letter-spacing:.08em!important}.iw-applied-discount{display:flex!important;align-items:center!important;justify-content:space-between!important;gap:8px!important;margin:0 0 10px!important;font-size:11px!important}.iw-applied-discount .discounts{margin:0!important}.iw-applied-discount>[data-iw-discount-remove]{border:0!important;background:transparent!important;color:#C8286E!important;text-decoration:underline!important;cursor:pointer!important}[data-iw-discount-message]{display:block;margin:6px 0 0;color:#9A5B51;font-size:11px}[data-iw-discount-message]:empty{display:none}
cart-drawer:not(.is-empty) .drawer__footer>.cart__ctas{margin:0!important;padding:0 16px 16px!important}.cart-drawer #CartDrawer-Checkout{width:100%!important;min-height:52px!important;margin:0!important;padding:14px 16px!important;display:flex!important;align-items:center!important;justify-content:center!important;gap:8px!important;border:1px solid #C8286E!important;border-radius:0!important;background:#C8286E!important;color:#fff!important;font:600 16px Manrope,Arial,sans-serif!important;letter-spacing:.08em!important;text-transform:uppercase!important;box-shadow:none!important}.cart-drawer #CartDrawer-Checkout:before,.cart-drawer #CartDrawer-Checkout:after{display:none!important}
@media(max-width:749px){cart-drawer:not(.is-empty) .drawer__inner{width:100vw!important}cart-drawer:not(.is-empty) .drawer__header{min-height:54px!important;padding:10px 16px!important;border-bottom:1px solid #C8C0B4!important}cart-drawer:not(.is-empty) #CartDrawer-CartItems{padding:0 14px!important}cart-drawer:not(.is-empty) .cart-item{grid-template-columns:80px minmax(0,1fr)!important;column-gap:14px!important;padding:14px 0!important}.cart-drawer .cart-item__media,.cart-drawer .cart-item__media .cart-item__link,.cart-drawer .cart-item__image{width:80px!important;height:96px!important}.cart-drawer .cart-drawer__footer{padding:14px!important}.cart-drawer .drawer__footer>.cart__ctas{padding:0 14px 14px!important}}
`;
    document.head.appendChild(style);
  }

  getSectionsToRender() {
    return [{ id: 'cart-drawer', selector: '#CartDrawer' }, { id: 'cart-icon-bubble' }];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) { this.activeElement = element; }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    ];
  }
}
customElements.define('cart-drawer-items', CartDrawerItems);
